import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, doc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { parseZugferdPdf } from '../../api/receivedInvoicesApi';
import { uploadIncomingInvoicePdf } from '../../api/incomingInvoicePdfApi';
import { ingestInboundUpload } from '../../api/inboundApi';
import { INBOUND_ENABLED_FLAG } from '../../config/inboundAccess';
import { ParsedZugferdData } from '../../types/receivedInvoiceTypes';

/**
 * Incoming-PDF-Archive feature flag (fail-closed: default off unless
 * explicitly 'true'). See architecture-incoming-pdf.md §12.
 */
const INCOMING_PDF_ARCHIVE_ENABLED = process.env.REACT_APP_INCOMING_PDF_ARCHIVE_ENABLED === 'true';

/**
 * Loop-A upload weiche (§5.11, exclusive): while `INBOUND_ENABLED` is on, the
 * manual upload runs through the server WORM pipeline (`ingestInboundUpload` →
 * `inbound_documents`) INSTEAD of the IPA client path. The two are mutually
 * exclusive — the same document never lands in both regimes (no double archive).
 * Module constant → the flag-off build collapses to the exact IPA behaviour.
 *
 * BEWUSST weiterhin am reinen `INBOUND_ENABLED_FLAG` (Test-Account-Beta-Gate,
 * NICHT auf `canUseInbound`/Beta-Allowlist umgestellt): dies ist kein reines
 * Sichtbarkeits-Gate, sondern eine funktionale Weiche, welcher Backend-Pfad
 * (WORM-Archiv vs. Client-IPA-Parse) aufgerufen wird. Der Server-Callable
 * `ingestInboundUpload` (functions/src/inbound/ingestInboundUpload.ts) prüft
 * ausschließlich `INBOUND_ENABLED` — keinen Beta-UID-Bypass.
 */
const INBOUND_ACCEPT = '.pdf,.xml,.png,.jpg,.jpeg,.tif,.tiff';

/** Metadata passed alongside the parsed ZUGFeRD data (architecture-incoming-pdf.md §4.5). */
export interface ReceivedInvoiceUploadMeta {
  filename: string;
  /** Pre-allocated `received_invoices` Firestore document ID. */
  receivedInvoiceId: string;
  /** Storage path of the archived original PDF, or `null` if flag off / upload failed. */
  pdfStorageRef: string | null;
  /** Byte size of the archived PDF, or `null` if flag off / upload failed. */
  byteSize: number | null;
}

/** Options for {@link useReceivedInvoiceUpload}. */
export interface UseReceivedInvoiceUploadOptions {
  onUploadSuccess: (data: ParsedZugferdData, meta: ReceivedInvoiceUploadMeta) => void;
  /**
   * Loop-A weiche (§5.11): called instead of `onUploadSuccess` when
   * `INBOUND_ENABLED` is on and the file was ingested via the server WORM
   * pipeline. `deferred:true` means archived-but-parse-postponed (fair use).
   * Optional — the flag-off build never calls it.
   */
  onInboundUploadSuccess?: (result: { inboundDocumentId: string; deferred: boolean }) => void;
}

/** Return value of {@link useReceivedInvoiceUpload}. */
export interface UseReceivedInvoiceUpload {
  /** Runs the Loop-A weiche for a single file (WORM ingest OR IPA client parse). */
  processFile: (file: File) => Promise<void>;
  /** `true` while a file is being ingested/parsed. */
  loading: boolean;
  /** Last error message (already localised), or `null`. */
  error: string | null;
  /** Clears the current error. */
  clearError: () => void;
  /** `accept` attribute for the file input — flag-dependent (`.pdf` vs. the full inbound list). */
  accept: string;
}

/**
 * Extracts the manual-upload logic of `ReceivedInvoiceUpload` (Loop-A weiche,
 * §5.11) into a reusable hook so both the full dropzone
 * (`ReceivedInvoiceUpload`) and the compact `CaptureBar` share ONE
 * implementation with byte-identical behaviour. The DOM/UI stays with the
 * consumer; this hook owns only the async processing + loading/error state.
 *
 * The `accept` string and the `INBOUND_ENABLED_FLAG` branch are read live on
 * every render/call so a runtime flag flip (or a test getter mock) takes
 * effect without a reload — matching the pre-extraction component exactly.
 *
 * @param opts - success callbacks for the two mutually exclusive paths.
 * @returns processing function + loading/error state + the live `accept` string.
 */
export function useReceivedInvoiceUpload(opts: UseReceivedInvoiceUploadOptions): UseReceivedInvoiceUpload {
  const { onUploadSuccess, onInboundUploadSuccess } = opts;
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live flag read (see JSDoc): recomputed each render so a getter-based test
  // mock / runtime flip is honoured.
  const accept = INBOUND_ENABLED_FLAG ? INBOUND_ACCEPT : '.pdf';

  const clearError = () => setError(null);

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Strip the data-URL prefix: "data:application/pdf;base64,..."
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /**
   * Loop-A weiche (§5.11): server WORM ingest. Reads the file as base64 and
   * hands it to `ingestInboundUpload` — NO client parse, NO client Storage
   * upload, NO `received_invoices` write (that path is skipped entirely to
   * prevent a double archive). Any file type is allowed; the server classifies.
   */
  const processInboundUpload = async (file: File): Promise<void> => {
    setError(null);
    setLoading(true);
    try {
      const base64 = await fileToBase64(file);
      const result = await ingestInboundUpload(
        base64,
        file.name,
        file.type || 'application/octet-stream',
      );
      onInboundUploadSuccess?.(result);
    } catch (err: any) {
      setError(
        err?.message ||
          t('receivedInvoices.parseError', 'Die Datei konnte nicht hochgeladen werden.'),
      );
    } finally {
      setLoading(false);
    }
  };

  const processFile = async (file: File): Promise<void> => {
    // Loop-A weiche (§5.11): flag on → server WORM path, exclusive.
    if (INBOUND_ENABLED_FLAG) {
      await processInboundUpload(file);
      return;
    }
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError(t('receivedInvoices.onlyPdf', 'Bitte laden Sie eine PDF-Datei hoch.'));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const base64 = await fileToBase64(file);
      const data = await parseZugferdPdf(base64);

      // Pre-allocate the received_invoices doc ID regardless of the flag, so
      // the meta signature is uniform (architecture-incoming-pdf.md §4.5/§7).
      const receivedInvoiceId = doc(collection(db, 'received_invoices')).id;

      let pdfStorageRef: string | null = null;
      let byteSize: number | null = null;

      if (INCOMING_PDF_ARCHIVE_ENABLED) {
        try {
          const uploadResult = await uploadIncomingInvoicePdf(file, receivedInvoiceId);
          pdfStorageRef = uploadResult.pdfStorageRef;
          byteSize = uploadResult.byteSize;
        } catch (uploadErr: any) {
          // Fail-open for the business function (§ IPA-3 DoD): the parsed
          // invoice can still be saved without an archived PDF. Report the
          // problem to the user, but do NOT abort the parse flow.
          setError(
            uploadErr?.message ||
              t(
                'receivedInvoices.pdfArchiveError',
                'Die PDF konnte nicht revisionssicher archiviert werden. Die Rechnungsdaten wurden trotzdem übernommen.',
              ),
          );
        }
      }

      onUploadSuccess(data, { filename: file.name, receivedInvoiceId, pdfStorageRef, byteSize });
    } catch (err: any) {
      setError(
        err?.message ||
          t('receivedInvoices.parseError', 'Die PDF konnte nicht als ZUGFeRD-Dokument gelesen werden.'),
      );
    } finally {
      setLoading(false);
    }
  };

  return { processFile, loading, error, clearError, accept };
}

export default useReceivedInvoiceUpload;
